import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Skeleton,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ResidenceBaseBlock } from "../components/ResidenceBaseBlock";
import { ResidenceEnergyBlock } from "../components/ResidenceEnergyBlock";
import { ResidenceGeneralBlock } from "../components/ResidenceGeneralBlock";
import { ScenarioCard } from "../components/ScenarioCard";
import { Page } from "../compositions/Page";
import {
  currentSituationAtom,
  SituationModal,
} from "../compositions/modals/SituationModal";
import { selectedScenariosAtom } from "../shared/state/atoms";
import {
  getResidenceType,
  getScenarioList,
  ResidenceTypeModel,
  ScenarioModel,
} from "../clients/KompasApi";
import { usePagination } from "../shared/hooks/usePagination";

export function ResidencePage() {
  const params = useParams<{ residenceId: string; situationId: string }>();
  const navigate = useNavigate();

  const [residenceType, setResidenceType] = useState<ResidenceTypeModel>();
  const [scenarios, setScenarios] = useState<ScenarioModel[]>([]);

  const [loadingResidenceType, setLoadingResidenceType] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [loadingSituation, setLoadingSituation] = useState(false);

  const [totalScenarioCount, setTotalScenarioCount] = useState(0);

  const [currentSituation] = useAtom(currentSituationAtom);

  const [selectedScenarios] = useAtom(selectedScenariosAtom);

  const { offset, setOffset, limit, loadMore } = usePagination("Scenarios");

  const loadingScenarioCards = useMemo(
    () => loadingScenarios || loadingSituation,
    [loadingScenarios, loadingSituation]
  );

  const residenceTypeId = useMemo(
    () => Number(params.residenceId ?? "0"),
    [params.residenceId]
  );

  useEffect(() => {
    if (!residenceTypeId) return;

    const abortController = new AbortController();

    setLoadingResidenceType(true);

    getResidenceType({
      path: { id: residenceTypeId },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        setResidenceType(data);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        throw error;
      })
      .finally(() => {
        setLoadingResidenceType(false);
      });

    return () =>
      abortController.abort("Component unmounted or residenceId changed");
  }, [residenceTypeId]);

  useEffect(
    () => {
      if (!currentSituation) return;

      setTotalScenarioCount(currentSituation?.scenarioCount ?? 0);
      setOffset(0);
      setScenarios([]);
      setLoadingSituation(false);
    },
    [currentSituation, setOffset]
  );

  useEffect(() => {
    if (!currentSituation) return;

    const abortController = new AbortController();

    setLoadingScenarios(true);

    getScenarioList({
      path: { residenceTypeId, situationId: currentSituation.id },
      query: { Limit: limit, Offset: offset },
      signal: abortController.signal,
      throwOnError: true,
    })
      .then(({ data }) => {
        if (abortController.signal.aborted) return;

        if (data) {
          setScenarios((prev) => {
            prev.splice(offset, limit, ...(data.items ?? []));
            return prev.slice(0, offset + limit);
          });
          setTotalScenarioCount(
            data.totalCount ?? currentSituation.scenarioCount
          );
        }
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        throw error;
      })
      .finally(() => setLoadingScenarios(false));

    return () =>
      abortController.abort("Component unmounted or url parameters changed");
  }, [residenceTypeId, currentSituation, offset, limit]);

  return (
    <Page flex>
      <Box maxW="1400px" mx="auto">
        <Box
          color="brand.gray.120"
          cursor="pointer"
          display="inline-block"
          fontWeight="500"
          onClick={() => navigate("/")}
        >
          <Image
            display="inline-block"
            filter="var(--filter-gray-120)"
            src="/svg/chevron.svg"
            transform="rotate(-90deg) translateX(-2px)"
          />
          Wijzig woningtype
        </Box>
        <Heading as="h1" fontSize={30} fontWeight="bold" mt={4}>
          Verduurzamingsscenario&apos;s voor woningtype:
          <Text as="span" color="brand.red.100" mx={1}>
            {residenceType?.name}
          </Text>
        </Heading>
        {!!residenceType && <SituationModal residence={residenceType} />}
        <Flex gap={6} mt={6}>
          <Flex flex={2} flexDir="column" gap={1} w={400}>
            <Heading as="h2" fontSize={16} fontWeight={500} mb={3}>
              Huidige situatie
            </Heading>
            <Box
              bg="transparent"
              border="4px solid white"
              borderRadius={4}
              boxShadow="0px 2px 4px 0px rgba(25, 25, 25, 0.04)"
            >
              {loadingSituation || (loadingResidenceType && <Spinner />)}
              {!!(currentSituation && residenceType) && (
                <ResidenceBaseBlock
                  residenceType={residenceType}
                  situation={currentSituation}
                />
              )}
            </Box>
            <ResidenceGeneralBlock
              calculation={currentSituation?.calculation}
            />
            <ResidenceEnergyBlock calculation={currentSituation?.calculation} />
          </Flex>
          <Flex flex={5} flexDir="column" gap={4} mb={8}>
            <Heading as="h2" fontSize={16} fontWeight={500}>
              Mogelijke verduurzamingsscenario&apos;s:{" "}
              {currentSituation?.scenarioCount ?? 0}
            </Heading>
            {!!(scenarios.length && currentSituation?.calculation) &&
              scenarios.map((s) => (
                <ScenarioCard
                  key={s.id}
                  scenario={s}
                  situation={currentSituation}
                />
              ))}
            {scenarios.length === 0 && !loadingScenarioCards && (
              <Text color="brand.gray.100" fontSize={14}>
                Geen scenario&apos;s gevonden voor de huidige situering van dit
                woningtype.
              </Text>
            )}
            {scenarios.length < totalScenarioCount && (
              <Button disabled={loadingScenarioCards} onClick={loadMore}>
                Meer laden
              </Button>
            )}
            {loadingScenarioCards && <Skeleton h={200} />}
          </Flex>
        </Flex>
      </Box>
      <Box bottom={0} pos="fixed" right={20}>
        <Button
          isDisabled={selectedScenarios.length === 0}
          onClick={() => navigate(`./compare/${selectedScenarios.join("&")}`)}
        >
          Vergelijk scenario&apos;s{" "}
          {!!selectedScenarios.length && (
            <Box
              bg="white"
              borderRadius="50%"
              color="brand.red.100"
              fontSize="14px"
              h={4}
              ml={2}
              w={4}
            >
              {selectedScenarios.length}
            </Box>
          )}
        </Button>
      </Box>
    </Page>
  );
}
